/**
 * Seed content + query helpers.
 *
 * The same module drives:
 *  - the in-memory repository (so the app runs with zero backend config), and
 *  - the meta server's route lookup (so crawlers/social get correct tags).
 *
 * Every slug here is produced by `slugify`, including the Telugu-titled article,
 * which gets a Latin slug via the `latin` hint — the fix for B3/B4.
 */
import { slugify } from './slug.js';

const now = Date.parse('2026-10-09T12:00:00Z');
const iso = (d) => new Date(d).toISOString();
const days = (n) => iso(now - n * 86400000);
const inDays = (n) => iso(now + n * 86400000);

export const LANGUAGES = [
  { code: 'en', name: 'English', direction: 'ltr' },
  { code: 'te', name: 'Telugu', direction: 'ltr' },
  { code: 'hi', name: 'Hindi', direction: 'ltr' },
];

export const REGIONS = [
  { code: 'IN', name: 'India' },
  { code: 'IN-TG', name: 'Telangana' },
  { code: 'IN-AP', name: 'Andhra Pradesh' },
];

export const CATEGORIES = [
  { id: 'cat-literature', name: 'Literature', slug: 'literature', description: 'Literary criticism, essays and bhasha writing.', sortOrder: 1 },
  { id: 'cat-interviews', name: 'Interviews', slug: 'interviews', description: 'Conversations with the people making culture.', sortOrder: 2 },
  { id: 'cat-articles', name: 'Articles', slug: 'articles', description: 'Reportage and analysis.', sortOrder: 3 },
  { id: 'cat-health', name: 'Health', slug: 'health', description: 'Medicine, wellbeing and public health.', sortOrder: 4 },
  { id: 'cat-entertainment', name: 'Entertainment', slug: 'entertainment', description: 'Film, music and the stage.', sortOrder: 5 },
  { id: 'cat-sports', name: 'Sports', slug: 'sports', description: 'Sport, played and watched.', sortOrder: 6 },
  { id: 'cat-lifestyle', name: 'Lifestyle', slug: 'lifestyle', description: 'Food, travel and living well.', sortOrder: 7 },
  { id: 'cat-telugu', name: 'Telugu', slug: 'telugu', description: 'Telugu-language writing and regional reporting.', sortOrder: 8 },
];

export const AUTHORS = [
  { id: 'au-1', name: 'Iqbal Chand', slug: 'iqbal-chand', bio: 'Editor-at-large. Writes on literature and the Indian canon.', avatarUrl: '' },
  { id: 'au-2', name: 'Lakshmi Rao', slug: 'lakshmi-rao', bio: 'Senior correspondent covering health and science.', avatarUrl: '' },
  { id: 'au-3', name: 'Vikram Menon', slug: 'vikram-menon', bio: 'Sports and culture writer.', avatarUrl: '' },
];

function A(obj) {
  return {
    dek: '',
    body: '',
    coverImageUrl: '',
    languageCode: 'en',
    regionCode: '',
    tags: [],
    scheduledFor: null,
    createdAt: obj.publishedAt || days(30),
    updatedAt: obj.publishedAt || days(30),
    revision: 1,
    viewCount: 0,
    seo: { title: '', description: '', canonical: '', ogImage: '', noindex: false },
    ...obj,
  };
}

export const ARTICLES = [
  A({
    id: 'ar-1',
    title: 'The Blue Hill of the Goddess',
    slug: slugify('The Blue Hill of the Goddess'),
    dek: 'At Kamakhya Temple on Nilachal Hill in Guwahati, the divine feminine is worshipped without an image.',
    body: 'HIGH ABOVE the Brahmaputra, where the river turns to beaten silver in the early light, a hill rises out of Guwahati like a held breath.\n\nThe people of Assam call it Nilachal, the Blue Hill. At its crown stands Kamakhya Temple, among the oldest and most revered seats of Shakti worship anywhere in the world.\n\nHere the goddess is not represented. She is present.',
    coverImageUrl: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-articles',
    authorId: 'au-1',
    tags: ['assam', 'temples', 'culture'],
    status: 'published',
    publishedAt: days(21),
    viewCount: 917,
    seo: { title: 'The Blue Hill of the Goddess', description: 'A pilgrimage to Kamakhya, where the divine feminine is honoured without an image.' },
  }),
  A({
    id: 'ar-2',
    title: 'A Himalayan Silence: A Tribute to Nana Patekar',
    slug: slugify('A Himalayan Silence: A Tribute to Nana Patekar'),
    dek: 'Nana Patekar: the man whose silence spoke louder than words.',
    body: 'He was a Marathi theatre actor first, and that is where the discipline comes from.\n\nThe stage teaches you that an audience can be held without a single line.',
    coverImageUrl: 'https://images.unsplash.com/photo-1594909122845-11baa439b7bf?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-entertainment',
    authorId: 'au-3',
    tags: ['cinema', 'marathi', 'tribute'],
    status: 'published',
    publishedAt: days(1),
    viewCount: 512,
  }),
  A({
    id: 'ar-3',
    title: 'Classics — The Idiot: A Meditation on Goodness in a Fallen World',
    slug: slugify('Classics - The Idiot: A Meditation on Goodness in a Fallen World'),
    dek: 'Dostoevsky’s Prince Myshkin remains the most radical figure in the nineteenth-century novel.',
    body: 'Goodness, in Dostoevsky, is not a virtue so much as a provocation.',
    coverImageUrl: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-literature',
    authorId: 'au-1',
    tags: ['dostoevsky', 'classics'],
    status: 'published',
    publishedAt: days(9),
    viewCount: 233,
  }),
  A({
    id: 'ar-4',
    title: 'నటనలో రౌద్రం... నిజ జీవితంలో కారుణ్యం',
    // Non-Latin title: the Latin hint supplies a readable, URL-safe slug.
    slug: slugify('నటనలో రౌద్రం నిజ జీవితంలో కారుణ్యం', { latin: 'Rage on stage, compassion in life', fallback: 'telugu-feature' }),
    dek: 'నటనలో రౌద్రం, నిజ జీవితంలో కారుణ్యం — ఒక నటుడి రెండు ముఖాలు.',
    body: 'నటనలో రౌద్రం కనిపిస్తుంది. నిజ జీవితంలో కారుణ్యం మిగులుతుంది.',
    coverImageUrl: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-telugu',
    authorId: 'au-2',
    languageCode: 'te',
    regionCode: 'IN-TG',
    tags: ['telugu', 'cinema'],
    status: 'published',
    publishedAt: days(2),
    viewCount: 604,
    seo: { title: 'రౌద్రం నటనలో, కారుణ్యం జీవితంలో', description: 'ఒక నటుడి వేదిక మరియు జీవితం మధ్య దూరం గురించి.' },
  }),
  A({
    id: 'ar-5',
    title: 'The Silent Epidemic: How Earphones Are Stealing Gen Z’s Hearing',
    slug: slugify('The Silent Epidemic: How Earphones Are Stealing Gen Z’s Hearing'),
    dek: 'Noise-induced hearing loss is rising among young adults, and it is almost entirely preventable.',
    body: 'The damage is cumulative and painless, which is precisely why it goes unnoticed.',
    coverImageUrl: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-health',
    authorId: 'au-2',
    tags: ['hearing', 'public health'],
    status: 'published',
    publishedAt: days(3),
    viewCount: 388,
  }),
  A({
    id: 'ar-6',
    title: 'The Golden Age of Indian Hockey: Memories and Legacy',
    slug: slugify('The Golden Age of Indian Hockey: Memories and Legacy'),
    dek: 'Eight Olympic golds, and the generation that made the world watch.',
    body: 'The 1948 and 1952 sides are still the reference point for Indian hockey.',
    coverImageUrl: 'https://images.unsplash.com/photo-1517747614396-d21a78b850e8?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-sports',
    authorId: 'au-3',
    tags: ['hockey', 'olympics'],
    status: 'published',
    publishedAt: days(6),
    viewCount: 276,
  }),
  A({
    id: 'ar-7',
    title: 'Mysore Pak: The Golden Heritage of Royal Indian Sweets',
    slug: slugify('Mysore Pak: The Golden Heritage of Royal Indian Sweets'),
    dek: 'A palace kitchen accident that became a national dessert.',
    body: 'Ghee, gram flour, sugar. The variables are few; the margins are everything.',
    coverImageUrl: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-lifestyle',
    authorId: 'au-2',
    tags: ['food', 'karnataka'],
    status: 'published',
    publishedAt: days(8),
    viewCount: 421,
  }),
  A({
    id: 'ar-8',
    title: 'The New Indian Canon: How Evolving Literary Trends Are Reshaping a Diverse Society',
    // Regenerated with the correct hyphenation — note there is no epoch suffix.
    slug: slugify('The New Indian Canon: How Evolving Literary Trends Are Reshaping a Diverse Society'),
    dek: 'Translation, self-publishing and the vernacular boom are rewriting what “Indian writing” means.',
    body: 'For most of the last century, the Indian novel in English set the terms. That is no longer true.',
    coverImageUrl: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1600&q=70',
    categoryId: 'cat-literature',
    authorId: 'au-1',
    tags: ['publishing', 'translation'],
    status: 'published',
    publishedAt: days(14),
    viewCount: 189,
  }),
  A({
    id: 'ar-9',
    title: 'What the Monsoon Did to the Western Ghats',
    slug: slugify('What the Monsoon Did to the Western Ghats'),
    dek: 'A field report on landslides, replanting and the slow work of recovery.',
    body: 'The ghats absorb more rain than almost anywhere on earth, and give it back all at once.',
    categoryId: 'cat-articles',
    authorId: 'au-2',
    status: 'in_review',
    publishedAt: null,
    createdAt: days(2),
    updatedAt: days(1),
  }),
  A({
    id: 'ar-10',
    title: 'The Long Read: A Century of Telugu Print',
    slug: slugify('The Long Read: A Century of Telugu Print'),
    dek: 'From letterpress weeklies to the algorithmic feed.',
    body: 'The Telugu press built a public sphere before it built a market.',
    categoryId: 'cat-telugu',
    authorId: 'au-1',
    languageCode: 'te',
    regionCode: 'IN-AP',
    status: 'scheduled',
    scheduledFor: inDays(3),
    publishedAt: null,
    createdAt: days(5),
    updatedAt: days(1),
  }),
];

export const MEDIA = [
  { id: 'me-1', fileName: 'the-blue-hill.jpg', url: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=800&q=60', mimeType: 'image/jpeg', sizeBytes: 182400, altText: 'Kamakhya Temple at dusk', credit: 'Unsplash', createdAt: days(22) },
  { id: 'me-2', fileName: 'nana-patekar.jpg', url: 'https://images.unsplash.com/photo-1594909122845-11baa439b7bf?auto=format&fit=crop&w=800&q=60', mimeType: 'image/jpeg', sizeBytes: 145000, altText: 'Portrait of an actor', credit: 'Unsplash', createdAt: days(3) },
  { id: 'me-3', fileName: 'monsoon-ghats.jpg', url: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=800&q=60', mimeType: 'image/jpeg', sizeBytes: 210300, altText: 'Misty hillside', credit: 'Unsplash', createdAt: days(2) },
];

export const TEAM = [
  { userId: 'u-1', name: 'Iqbal Chand', email: 'kmec1983@gmail.com', role: 'admin', status: 'active' },
  { userId: 'u-2', name: 'Lakshmi Rao', email: 'lakshmi@thestoryline.xyz', role: 'editor', status: 'active' },
  { userId: 'u-3', name: 'Vikram Menon', email: 'vikram@thestoryline.xyz', role: 'writer', status: 'invited' },
];

export const AUDIT_LOG = [
  { id: 'al-1', at: days(1), actor: 'Iqbal Chand', action: 'article.publish', resource: 'The Blue Hill of the Goddess', outcome: 'ok' },
  { id: 'al-2', at: days(2), actor: 'Lakshmi Rao', action: 'article.submit_review', resource: 'What the Monsoon Did to the Western Ghats', outcome: 'ok' },
  { id: 'al-3', at: days(2), actor: 'Vikram Menon', action: 'article.create', resource: 'Mysore Pak: The Golden Heritage of Royal Indian Sweets', outcome: 'ok' },
];

export const SITE_SETTINGS = {
  title: 'The Story Line',
  tagline: 'Stories, art and culture, told well.',
  description: 'The Story Line is a literary, art and entertainment publication: essays, reviews, interviews and culture reporting, told well.',
  social: { facebook: '', twitter: '', instagram: '', youtube: '' },
  hideViewCounts: false,
  pauseArchival: true,
};

// ---------------------------------------------------------------------------
// Query helpers (pure; operate on the seed above)
// ---------------------------------------------------------------------------

const byId = (list, id) => list.find((x) => x.id === id) || null;

export function hydrate(article) {
  if (!article) return null;
  return {
    ...article,
    category: byId(CATEGORIES, article.categoryId),
    author: byId(AUTHORS, article.authorId),
  };
}

export function getCategoryBySlug(slug) {
  return CATEGORIES.find((c) => c.slug === slug) || null;
}

export function getAuthorBySlug(slug) {
  return AUTHORS.find((a) => a.slug === slug) || null;
}

export function listCategories() {
  return [...CATEGORIES].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function listArticles({ statuses, categoryId, query, limit = 50, offset = 0, sort = 'newest' } = {}) {
  let rows = ARTICLES.slice();
  if (statuses && statuses.length) rows = rows.filter((a) => statuses.includes(a.status));
  if (categoryId) rows = rows.filter((a) => a.categoryId === categoryId);
  if (query) {
    const q = query.toLowerCase();
    rows = rows.filter(
      (a) => a.title.toLowerCase().includes(q) || a.dek.toLowerCase().includes(q) || a.body.toLowerCase().includes(q),
    );
  }
  rows.sort((a, b) => {
    const av = new Date(a.publishedAt || a.scheduledFor || a.createdAt).getTime();
    const bv = new Date(b.publishedAt || b.scheduledFor || b.createdAt).getTime();
    return sort === 'oldest' ? av - bv : bv - av;
  });
  return rows.slice(offset, offset + limit).map(hydrate);
}

export function getArticleBySlug(slug) {
  const a = ARTICLES.find((x) => x.slug === slug);
  return a ? hydrate(a) : null;
}

export function allPublishedSlugs() {
  return ARTICLES.filter((a) => a.status === 'published').map((a) => a.slug);
}
